import { fetchAuth } from "../common/api";
import { fetchJsonCached, peekCachedJson, setCachedJson } from "../common/fetchJsonCache";

export const PLAYER_DETAILS_TTL_MS = 45_000;
export const PLAYER_DATES_TTL_MS = 5 * 60_000;
export const PLAYER_SESSION_TTL_MS = 30 * 60_000;
export const PLAYER_STATS_TTL_MS = 10 * 60_000;
export const PLAYER_DETAILS_FETCH_TIMEOUT_MS = 15000;
export const PLAYER_DETAILS_BACKGROUND_TIMEOUT_MS = 10000;

const AVATAR_MODEL_PATH = "avatar_compressed.glb";

let avatarGltfPromise = null;
let avatarGltf = null;
let dracoLoader = null;

export function playerDetailsPath(playerId, season) {
  return `/playersperfs/${playerId}?season=${season}`;
}

export function playerDatesPath(playerId, season) {
  return `/session_acceleration_sprint/dates/${playerId}?season=${season}`;
}

export function playerSprintPath(playerId, date, season, period = "Morning") {
  return `/session_acceleration_sprint_date/${playerId}?date=${encodeURIComponent(date)}&period=${period}&season=${season}`;
}

export function playerMusclePath(playerId, date) {
  return `/muscle_data/${playerId}?date=${date}`;
}

export function playerHistoryPath(playerId, date, season) {
  return `/playersperfs/${playerId}?date=${encodeURIComponent(date)}&season=${season}`;
}

export function peekPlayerDetails(playerId, season) {
  return peekCachedJson(playerDetailsPath(playerId, season));
}

export function peekPlayerDates(playerId, season) {
  return peekCachedJson(playerDatesPath(playerId, season));
}

export function peekPlayerSprint(playerId, date, season) {
  return peekCachedJson(playerSprintPath(playerId, date, season));
}

export function peekPlayerMuscle(playerId, date) {
  return peekCachedJson(playerMusclePath(playerId, date));
}

export function peekAvatarGltf() {
  return avatarGltf;
}

export function prefetchPlayerDetailsData(playerId, season) {
  if (!playerId || !season) return;
  fetchJsonCached(playerDetailsPath(playerId, season), {
    ttlMs: PLAYER_DETAILS_TTL_MS,
    timeoutMs: PLAYER_DETAILS_FETCH_TIMEOUT_MS,
  }).catch(() => {});
  fetchJsonCached(playerDatesPath(playerId, season), {
    ttlMs: PLAYER_DATES_TTL_MS,
    timeoutMs: PLAYER_DETAILS_BACKGROUND_TIMEOUT_MS,
  }).catch(() => {});
}

export function prefetchPlayerDetailsPage(loader) {
  if (typeof loader !== "function") return Promise.resolve();
  return loader().catch(() => {});
}

export function prefetchPlayerTab(tab) {
  switch (tab) {
    case "twin":
    case "history":
      return Promise.all([
        import("./FatigueChart"),
        import("./IntensityMusclesChart"),
        import("./MuscleIndexCharts"),
        import("./ForceVelocityChart"),
        import("./TrainabilityCard"),
        import("../bodyMap/FBXViewer"),
      ]).catch(() => {});
    case "graphs":
      return import("./MuscleGroupGraph").catch(() => {});
    case "stats":
      return Promise.all([
        import("./PlayerAvailabilityStats"),
        import("./PlayerTransfermarktStats"),
      ]).catch(() => {});
    case "simulation":
      return import("./PlayerMotion").catch(() => {});
    case "wellness":
      return import("./PlayerWellnessTab").catch(() => {});
    default:
      return Promise.resolve();
  }
}

export async function loadAvatarGltf({ signal, timeoutMs = PLAYER_DETAILS_BACKGROUND_TIMEOUT_MS } = {}) {
  if (avatarGltf) return avatarGltf;
  if (avatarGltfPromise) return avatarGltfPromise;

  avatarGltfPromise = (async () => {
    const [{ GLTFLoader }, { DRACOLoader }] = await Promise.all([
      import("three/examples/jsm/loaders/GLTFLoader"),
      import("three/examples/jsm/loaders/DRACOLoader"),
    ]);

    const res = await fetchAuth(`/fbx_files/presign/${AVATAR_MODEL_PATH}`, {
      signal,
      timeoutMs,
    });
    if (!res?.ok) {
      throw new Error(`Avatar presign failed (${res?.status || 0})`);
    }
    const { url } = await res.json();
    if (!url) throw new Error("Avatar presign missing url");

    const loader = new GLTFLoader();
    if (!dracoLoader) {
      dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("/draco/");
      dracoLoader.preload();
    }
    loader.setDRACOLoader(dracoLoader);

    const gltf = await new Promise((resolve, reject) => {
      loader.load(url, resolve, undefined, reject);
    });
    avatarGltf = gltf;
    return gltf;
  })().catch((error) => {
    avatarGltfPromise = null;
    throw error;
  });

  return avatarGltfPromise;
}

export function rememberPlayerDetails(playerId, season, data) {
  if (!playerId || !season || !data) return;
  setCachedJson(playerDetailsPath(playerId, season), data, PLAYER_DETAILS_TTL_MS);
}
